import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { motion, AnimatePresence } from "framer-motion";
import FullButton from "../Buttons/FullButton";

// Local images (desktop)
import landscape1 from "../../assets/img/landscape1.png";
import landscape2 from "../../assets/img/landscape2.png";
import landscape3 from "../../assets/img/landscape3.png";
// Local images (mobile)
import portrait1 from "../../assets/img/portrait1.png";
import portrait2 from "../../assets/img/portrait2.png";
import portrait3 from "../../assets/img/portrait3.png";

const ROTATING_WORDS = [ "Effort", "Growth", "Trust"];

export default function Header() {
  const desktopImgs = [landscape1, landscape2, landscape3];
  const mobileImgs = [portrait1, portrait2, portrait3];

  const [currentBg, setCurrentBg] = useState(0);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [wordIndex, setWordIndex] = useState(0);

  // Resize listener
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Background slider
  useEffect(() => {
    const id = setInterval(() => {
      setCurrentBg((p) => (p + 1) % 3);
    }, 6000);
    return () => clearInterval(id);
  }, []);

  // Rotate words
  useEffect(() => {
    const id = setInterval(() => {
      setWordIndex((p) => (p + 1) % ROTATING_WORDS.length);
    }, 3000);
    return () => clearInterval(id);
  }, []);

  const images = isMobile ? mobileImgs : desktopImgs;

  return (
    <Section>
      {/* Background */}
      <AnimatePresence mode="wait">
        <Bg
          key={currentBg + (isMobile ? "-m" : "-d")}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 1.2 }}
          style={{ backgroundImage: `url(${images[currentBg]})` }}
        />
      </AnimatePresence>
      <Overlay />

      {/* Content */}
      <Content>
        <CompanyName>Sama Al Falah Computer TR LLC</CompanyName>

        <Tagline>
          Your Gateway to Global Tech{" "}
          <WordSlot>
            <AnimatePresence mode="sync">
              <Word
                key={wordIndex}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.45 }}
              >
                {ROTATING_WORDS[wordIndex]}
              </Word>
            </AnimatePresence>
          </WordSlot>
        </Tagline>

        <Blurb>
          We distribute refurbished laptops, desktops, and IT hardware.
          <strong> Sama Al Falah</strong> connects <strong>US suppliers</strong>{" "}
          to global markets with seamless logistics and trusted partnerships.
        </Blurb>

        <CTA>
          <motion.div whileHover={{ scale: 1.05 }}>
            <FullButton title="Partner With Us" />
          </motion.div>
        </CTA>
      </Content>

      {/* Navigation dots */}
      <Dots>
        {[0, 1, 2].map((i) => (
          <Dot
            key={i}
            isActive={i === currentBg}
            onClick={() => setCurrentBg(i)}
          />
        ))}
      </Dots>
    </Section>
  );
}

/* ================= STYLES ================= */

const Section = styled.section`
  position: relative;
  width: 100%;
  min-height: 100vh;
  display: flex;
  align-items: center;
  overflow: hidden;
`;

const Bg = styled(motion.div)`
  position: absolute;
  inset: 0;
  background-size: cover;
  background-position: center;
  z-index: 0;
`;

const Overlay = styled.div`
  position: absolute;
  inset: 0;
  background: rgba(10, 25, 47, 0.65);
  z-index: 1;
`;

const Content = styled.div`
  position: relative;
  z-index: 2;
  max-width: 960px;
  padding: 0 2.5rem;
  margin-left: 3vw;
  color: #fff;

  @media (max-width: 768px) {
    margin: 0 auto;
    text-align: center;
    padding: 0 1.25rem;
  }
`;

const CompanyName = styled.h1`
  font-size: clamp(2rem, 3.8vw, 3rem);
  font-weight: 800;
  color: #5fd1f9;
  margin-bottom: 0.75rem;
`;

const Tagline = styled.h2`
  font-size: clamp(1.25rem, 2.2vw, 1.8rem);
  font-weight: 700;
  color: #64ffda;
  display: flex;
  align-items: baseline;
  gap: 0.5rem;
  margin-bottom: 1.25rem;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    justify-content: center;
    text-align: center;
  }
`;

const WordSlot = styled.span`
  position: relative;
  display: inline-block;
  height: 1.2em;
  min-width: 7ch;         /* 👈 ensures enough space for longest word */
  overflow: hidden;
  vertical-align: middle; /* aligns nicely with tagline */
`;

const Word = styled(motion.span)`
  position: absolute;
  top: 17%;
  left: 0;
  transform: translateY(-10%); /* perfectly center vertically */
  color: #ffd700;
  font-weight: 800;
  white-space: nowrap;
`;

const Blurb = styled.p`
  max-width: 800px;
  font-size: clamp(1rem, 1.3vw, 1.125rem);
  line-height: 1.7;
  color: #e8edf3;
  margin-bottom: 2rem;
`;

const CTA = styled.div`
  display: flex;
  justify-content: flex-start;
  @media (max-width: 768px) {
    justify-content: center;
  }
`;

const Dots = styled.div`
  position: absolute;
  bottom: 28px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 10px;
  z-index: 3;
`;

const Dot = styled.button`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: none;
  background: ${({ isActive }) => (isActive ? "#64ffda" : "#bbb")};
  cursor: pointer;
  transition: background 0.25s ease;
`;
